import { memo } from 'react';

import { SHARE_MODAL_SECTION_ROWS_CLASS_NAME } from '~/components/ShareModal/components/ShareModalRow';
import { ShareModalSectionLabel } from '~/components/ShareModal/components/ShareModalSectionLabel';
import { ShareBoardDesignBrandContextSection } from '~/components/SharePrivateBoardModal/ShareBoardDesignBrandContextSection';
import { ShareBoardDesignButtonsHeadlinesRow } from '~/components/SharePrivateBoardModal/ShareBoardDesignButtonsHeadlinesRow';
import { ShareBoardDesignCornerRoundingRow } from '~/components/SharePrivateBoardModal/ShareBoardDesignCornerRoundingRow';
import { ShareBoardDesignCoverButton } from '~/components/SharePrivateBoardModal/ShareBoardDesignCoverButton';
import { ShareBoardDesignHeaderBackgroundRow } from '~/components/SharePrivateBoardModal/ShareBoardDesignHeaderBackgroundRow';
import { ShareBoardDesignHeadlineFontRow } from '~/components/SharePrivateBoardModal/ShareBoardDesignHeadlineFontRow';
import { ShareBoardDesignLogoButton } from '~/components/SharePrivateBoardModal/ShareBoardDesignLogoButton';
import { ShareBoardDesignPageBackgroundRow } from '~/components/SharePrivateBoardModal/ShareBoardDesignPageBackgroundRow';
import { ShareBoardDesignPageThemeRow } from '~/components/SharePrivateBoardModal/ShareBoardDesignPageThemeRow';
import { ShareBoardDesignPageWidthRow } from '~/components/SharePrivateBoardModal/ShareBoardDesignPageWidthRow';
import { ShareBoardDesignPreview } from '~/components/SharePrivateBoardModal/ShareBoardDesignPreview';
import {
  ShareBoardDesignProvider,
  type ShareBoardDesignProviderProps,
} from '~/components/SharePrivateBoardModal/ShareBoardDesignProvider';

export type ShareBoardDesignTabProps = ShareBoardDesignProviderProps;

/**
 * Design tab for the board share modal. Thin composition layer: it wraps the board + share-link
 * inputs in {@link ShareBoardDesignProvider} and renders the Brand context section, live preview,
 * image pickers, and the ordered Colors / Page & Layout rows directly — each row reads what it
 * needs from `useShareBoardDesign()` rather than threading props, so rows can be reordered freely
 * here.
 */
export const ShareBoardDesignTab = memo(({ boardId, shortIdInfo }: ShareBoardDesignTabProps) => (
  <ShareBoardDesignProvider boardId={boardId} shortIdInfo={shortIdInfo}>
    <div className="flex flex-col gap-6" data-testid="SHARE_BOARD_DESIGN_TAB">
      <ShareBoardDesignBrandContextSection />
      <ShareBoardDesignPreview />
      <div className="flex gap-2">
        <ShareBoardDesignLogoButton />
        <ShareBoardDesignCoverButton />
      </div>
      <div className="flex flex-col gap-2">
        <ShareModalSectionLabel title="Colors" />
        <div className={SHARE_MODAL_SECTION_ROWS_CLASS_NAME}>
          <ShareBoardDesignPageThemeRow />
          <ShareBoardDesignHeaderBackgroundRow />
          <ShareBoardDesignButtonsHeadlinesRow />
          <ShareBoardDesignPageBackgroundRow />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <ShareModalSectionLabel title="Page & Layout" />
        <div className={SHARE_MODAL_SECTION_ROWS_CLASS_NAME}>
          <ShareBoardDesignHeadlineFontRow />
          <ShareBoardDesignCornerRoundingRow />
          <ShareBoardDesignPageWidthRow />
        </div>
      </div>
    </div>
  </ShareBoardDesignProvider>
));

ShareBoardDesignTab.displayName = 'ShareBoardDesignTab';
